<template>
  <div class="participants-checklist">
    <div class="participants-checklist-head">
      <b-form-checkbox
        :checked="allChecked"
        :indeterminate="!allChecked && visibleSelectedCount > 0"
        :disabled="!visibleStudents.length"
        @change="toggleAll"
      >
        <b>Select all</b>
      </b-form-checkbox>
      <!-- same scope as Select all: the classes on screen -->
      <span class="participants-checklist-count">{{ visibleSelectedCount }} / {{ visibleStudents.length }} selected</span>
      <b-form-input
        v-model="search"
        size="sm"
        class="participants-checklist-search"
        placeholder="Search student"
      ></b-form-input>
    </div>

    <p class="text-danger mb-0" v-if="!uniqueStudents.length">No Participants Found!</p>
    <p class="text-muted mb-0" v-else-if="!visibleClasses.length">No students found</p>

    <!-- one column per class, like the course faculty combinations popup -->
    <div class="participants-table-wrap" v-if="visibleClasses.length">
      <table class="table table-bordered mb-0">
        <thead>
          <tr>
            <th v-for="group in visibleClasses" :key="group.key" class="participants-class-head">
              {{ group.name }}
              <span v-if="group.programme_code" class="participants-class-code">{{ group.programme_code }}</span>
              <div class="participants-checklist-count">
                {{ classSelectedCount(group) }} / {{ group.students.length }} selected
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td v-for="group in visibleClasses" :key="group.key">
              <b-form-checkbox
                v-for="student in classStudents(group)"
                :key="student.id"
                :checked="isChecked(student)"
                :disabled="student.submitted"
                @change="toggleStudent(student, $event)"
              >
                <span v-if="student.roll_number">{{ student.roll_number }}-</span>{{ student.name }}
                <span v-if="student.submitted" class="participants-submitted"
                  v-b-tooltip.hover title="Already submitted, can't be removed">Submitted</span>
              </b-form-checkbox>
              <b-link v-if="expandedClass == group.key" class="participants-class-more"
                @click="expandedClass = null">Hide</b-link>
              <b-link v-else-if="group.students.length > previewCount" class="participants-class-more"
                @click="expandedClass = group.key">
                Load more ({{ group.students.length - previewCount }} more)
              </b-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  // students: [{id, name, roll_number, class_id, class_name}], value: selected student ids
  props: ["students", "value"],
  data() {
    return {
      search: "",
      previewCount: 5, // students shown under each class until Load more
      expandedClass: null, // class showing all its students, the other classes are hidden
    };
  },
  watch: {
    // search runs over all classes
    search() {
      this.expandedClass = null;
    },
  },
  computed: {
    selected() {
      return new Set(this.value);
    },
    // the API can list a student more than once, show each student once
    // and keep the submitted entry
    uniqueStudents() {
      const students = new Map();
      this.students.forEach((student) => {
        const existing = students.get(student.id);
        if (!existing || (student.submitted && !existing.submitted)) students.set(student.id, student);
      });
      return Array.from(students.values());
    },
    // students of the classes on screen (search / Load more), Select all works on these
    visibleStudents() {
      return this.visibleClasses.reduce((students, group) => students.concat(group.students), []);
    },
    visibleSelectedCount() {
      return this.visibleStudents.filter((student) => this.isChecked(student)).length;
    },
    allChecked() {
      return this.visibleStudents.length > 0 && this.visibleSelectedCount == this.visibleStudents.length;
    },
    // students under their class, classes and students in name / roll number order
    classes() {
      const classes = {};
      this.uniqueStudents.forEach((student) => {
        const key = student.class_id || "none";
        if (!classes[key]) {
          classes[key] = {
            key,
            name: student.class_name || "No class",
            programme_code: student.programme_code,
            students: [],
          };
        }
        classes[key].students.push(student);
      });
      return Object.values(classes)
        .sort((a, b) => {
          if (a.key == "none") return 1;
          if (b.key == "none") return -1;
          return a.name.localeCompare(b.name);
        })
        .map((group) => {
          group.students.sort((a, b) =>
            String(a.roll_number || "").localeCompare(String(b.roll_number || ""), undefined, { numeric: true }) ||
            a.name.localeCompare(b.name)
          );
          return group;
        });
    },
    filteredClasses() {
      const search = this.search.trim().toLowerCase();
      if (!search) return this.classes;
      return this.classes
        .map((group) => ({
          ...group,
          students: group.students.filter((student) =>
            `${student.roll_number || ""} ${student.name}`.toLowerCase().includes(search)
          ),
        }))
        .filter((group) => group.students.length);
    },
    visibleClasses() {
      if (this.expandedClass == null) return this.filteredClasses;
      return this.filteredClasses.filter((group) => group.key == this.expandedClass);
    },
  },
  methods: {
    classStudents(group) {
      if (this.expandedClass == group.key) return group.students;
      return group.students.slice(0, this.previewCount);
    },
    isChecked(student) {
      return this.selected.has(student.id);
    },
    classSelectedCount(group) {
      return group.students.filter((student) => this.isChecked(student)).length;
    },
    toggleStudent(student, checked) {
      this.update(checked ? [student.id] : [], checked ? [] : [student.id]);
    },
    // only the students on screen change, students who already submitted stay selected
    toggleAll(checked) {
      const ids = this.visibleStudents
        .filter((student) => !student.submitted)
        .map((student) => student.id);
      this.update(checked ? ids : [], checked ? [] : ids);
    },
    update(add, remove) {
      const selected = new Set(this.value);
      add.forEach((id) => selected.add(id));
      remove.forEach((id) => selected.delete(id));
      this.$emit("input", Array.from(selected));
    },
  },
};
</script>

<style scoped>
.participants-checklist-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-bottom: 12px;
}

.participants-checklist-search {
  width: 220px;
  margin-left: auto;
}

.participants-checklist-count {
  font-size: 12px;
  color: #718096;
}

/* scrolls on its own so the class headings stay on top */
.participants-table-wrap {
  max-height: 55vh;
  overflow: auto;
}

/* App.vue makes the heading row sticky with no z-index, so the student
   checkboxes (z-index 1) scrolled over it */
.participants-table-wrap thead tr {
  position: sticky;
  top: 0;
  z-index: 3;
}

.participants-class-head {
  min-width: 250px;
  background-color: #fff !important;
  background-clip: padding-box;
  /* bordered table borders don't stick, draw the bottom line */
  box-shadow: inset 0 -1px 0 #dee2e6;
}

.participants-class-code {
  margin-left: 6px;
  padding: 1px 8px;
  font-size: 11px;
  color: #4a5568;
  background: #edf2f7;
  border-radius: 999px;
}

.participants-submitted {
  margin-left: 4px;
  padding: 0 6px;
  font-size: 11px;
  color: #22753a;
  background: #e3f6e8;
  border-radius: 999px;
}

.participants-class-more {
  display: inline-block;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
}
</style>
